/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, UNPLACEHOLDER } from '@dm/core';
import { productModelE } from 'app/common/constants';
import { GlobalDataService, ToolService } from 'app/common/services';
import { type System } from 'app/common/types/mo';
import { type OEM } from 'app/common/types/oem';
import { SWITCH } from 'app/config';

@Injectable()
export class HelpMenuStrService {
    oem: OEM = (window as any).oem;

    i18nLabel = {
        quickStart: this.i18n.get('home.Help_quickstart_task_quickStart_label'),
        how2Provisioning: this.i18n.get('home.Help_quickstart_task_provision_label'),
        how2ProtectData: this.i18n.get('home.Help_quickstart_task_protectData_label'),
        help: this.i18n.get('home.Help_task_help_label'),
        onlineHlep: this.i18n.get('home.Help_task_onlineHelp_label'),
        support: this.i18n.get('home.Help_task_supportSite_label'),
        technicalSupport: this.i18n.get('home.Help_tecSupport_task_technicalSupport_label'),
        selfService: this.i18n.get('home.Help_tecSupport_task_selfServicePlatform_label'),
        bulletin: this.i18n.get('home.Help_tecSupport_task_serviceBulletin_label'),
        forum: this.i18n.get('home.Help_tecSupport_task_cummunityForum_label'),
        downloadCRUTool: this.i18n.get('home.Help_tecSupport_task_CRUReplaceTool_label'),
        userReplaceableUnit: this.i18n.get('home.Help_tecSupport_task_userReplaceUnit_label'),
        QandA: this.i18n.get('home.Help_QA_task_QA_label'),
        remoteDevice: this.i18n.get('home.Help_QA_task_cannotConnectRemoteDevice_label'),
        storagePool: this.i18n.get('home.Help_QA_task_cannotCreatePool_label'),
        about: this.i18n.get('home.Help_tecSupport_task_about_label'),
        urlLabel: this.i18n.get('home.Help_tecSupport_task_about_website_label'),
        url: this.i18n.get('home.Help_tecSupport_task_about_website_value'),
        warning: this.i18n.get('common.Main_Label_About_Content_Warning'),
        warningLabel: this.i18n.get('common.Main_Label_Warning'),
        copyright: this.i18n.get('common.copyright_label'),
        versionLabel: this.i18n.get('common.device_version_label'),
        helpTaskLabel: this.i18n.get('home.Help_task_lead_label'),
        supportEWebsite: this.i18n.get('common.supportE_website'),
        supportITWebsite: this.i18n.get('common.supportIT_website'),
        supportITMicroWebsite: this.i18n.get('common.supportIT_micro_website'),
        supportITOceanstorWebsite: this.i18n.get('common.supportIT_oceanstor_website'),
        forumEWebsite: this.i18n.get('common.forumE_website'),
        closeBtn: this.i18n.get('common.close_button'),
        supportOnlinetoolwebWebsite: this.i18n.get('common.supportOnlinetoolweb_website'),
        intelligentCustomerServiceLab: this.i18n.get('home.Help_tecSupport_task_intelCusService_label'),
        intelligentCustomerServiceTip: this.i18n.get('home.intelCusService_task_desc'),
        intelligentCustomerServiceQrcode: this.i18n.get('home.intelCusService_task_app_info'),
        intelligentCustomerServiceHyperlink: this.i18n.get('home.intelCusService_task_website_info'),
        intelligentCustomerServiceWebSite: this.i18n.get('home.intelCusService_task_website_link'),
    };

    language = this.i18n.language;
    isOceanProtectX8000 = this.toolService.isOceanProtectX8000();

    isCoverageStor = this.toolService.isCoverageStor();

    getHelpUrl = (): {
        support: string;
        selfService: string;
        bulletin: string;
        forum: string;
        downloadCRUTool: string;
        userReplaceableUnit: string;
    } => {
        const helpUrl = {
            support: `${this.i18nLabel.supportEWebsite + (this.language === 'ja' ? 'en' : this.language)}/index.html`,
            selfService: this.i18nLabel.supportITWebsite,
            bulletin: `${this.i18nLabel.supportEWebsite +
                (this.language === 'ja' ? 'en' : this.language)}/bulletins-product/warning-notices/solid-state-array-pid-1482606662246`,
            forum: this.i18nLabel.forumEWebsite + `${this.language === 'zh' ? 'zh/group/580879824984092672' : 'en/Storage/forum/891'}`,
            downloadCRUTool: `${this.i18nLabel.supportEWebsite +
                (this.language === 'ja' ? 'en' : this.language)}/cloud-storage/smartkit-pid-8576706/software`,
            userReplaceableUnit: '',
        };

        helpUrl.userReplaceableUnit = this.updateUserReplaceableUnit(this.globalDataService.getSystemInfo());

        if (SWITCH.IS_OCEAN_MICRO) {
            helpUrl.selfService = this.i18nLabel.supportITMicroWebsite;
        } else if (SWITCH.IS_CONVERGED_PROTECT) {
            helpUrl.selfService = this.i18nLabel.supportITOceanstorWebsite;
        }

        return helpUrl;
    };

    /**
     * 更新跳转链接
     * @param systemInfo 系统信息
     */
    updateUserReplaceableUnit = (systemInfo: System): string => {
        if (!systemInfo) {
            return '';
        }

        // 白牌不展示cru相关
        if (this.oem && this.oem.vendor !== 'huawei') {
            return '';
        }

        return this.getProductUserReplaceableUnit(Number(systemInfo.PRODUCTMODE));
    };

    /**
     * 从i18n中获取跳转链接
     * @param productModel 型号枚举
     */
    getProductUserReplaceableUnit = (productModel: number): string => {
        if (!productModel) {
            return '';
        }

        const i18nKeyStr = `common.EnumDefine_PRODUCT_CRUURL_MODE_E_${productModel}`;
        const cruUrl = this.i18n.get(i18nKeyStr);

        // 和i18nKey一致说明未获取到词条
        const urlDisabled = cruUrl === i18nKeyStr;

        if (urlDisabled) {
            return '';
        }

        return cruUrl;
    };

    constructor(
        private i18n: I18nService,
        private toolService: ToolService,
        private globalDataService: GlobalDataService,
    ) { }
}
